import { useEffect, useMemo, useState, type JSX } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, type Product, type Snippet } from '../api.js';
import { useActiveProfileId } from '../active-profile.js';
import { Icon } from '../components/Icon.js';
import { CopyCodeBlock } from '../components/ui.js';
import { Topbar } from './AdminLayout.js';
import { fmtAmountPlain, fmtDate } from '../utils/format.js';

export function ProductsList(): JSX.Element {
  const nav = useNavigate();
  const [items, setItems] = useState<Product[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [tab, setTab] = useState<'active' | 'inactive'>('active');
  const [snippetFor, setSnippetFor] = useState<Product | null>(null);

  const [activeProfileId] = useActiveProfileId();
  useEffect(() => {
    setItems(null);
    api.listProducts().then(setItems).catch((e: Error) => setErr(e.message));
  }, [activeProfileId]);

  const counts = useMemo(() => {
    if (!items) return { active: 0, inactive: 0 };
    let active = 0;
    let inactive = 0;
    for (const p of items) (p.isActive ? active++ : inactive++);
    return { active, inactive };
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const s = q.toLowerCase();
    const wantActive = tab === 'active';
    return items.filter(
      (p) => p.isActive === wantActive && (p.name + p.slug).toLowerCase().includes(s),
    );
  }, [items, q, tab]);

  return (
    <>
      <Topbar
        title="Продукты"
        actions={
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => nav('/admin/products/new')}
          >
            <Icon name="plus" size={13} />
            Новый продукт
          </button>
        }
      />
      <div style={{ padding: '24px 28px', flex: 1, overflowY: 'auto' }}>
        {err && (
          <div
            className="card"
            style={{
              padding: 14,
              marginBottom: 14,
              color: 'var(--danger-fg)',
              background: 'var(--danger-subtle-bg)',
            }}
          >
            {err}
          </div>
        )}
        <div className="card">
          <div style={{ padding: '0 20px' }}>
            <div className="tabs">
              <button
                type="button"
                className={'tab ' + (tab === 'active' ? 'active' : '')}
                onClick={() => setTab('active')}
              >
                Активные {items ? `(${counts.active})` : ''}
              </button>
              <button
                type="button"
                className={'tab ' + (tab === 'inactive' ? 'active' : '')}
                onClick={() => setTab('inactive')}
              >
                Выключенные {items ? `(${counts.inactive})` : ''}
              </button>
            </div>
          </div>
          <div className="card-header" style={{ gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
              <div style={{ position: 'relative', maxWidth: 320, flex: 1 }}>
                <div
                  style={{
                    position: 'absolute',
                    left: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--fg-subtle)',
                  }}
                >
                  <Icon name="search" size={14} />
                </div>
                <input
                  className="input"
                  placeholder="Искать по имени или URL…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  style={{ paddingLeft: 32 }}
                />
              </div>
            </div>
            <div style={{ fontSize: 13, color: 'var(--fg-subtle)' }}>
              {items
                ? `${filtered.length} из ${tab === 'active' ? counts.active : counts.inactive}`
                : '…'}
            </div>
          </div>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 74 }}></th>
                <th>Продукт</th>
                <th>URL</th>
                <th style={{ textAlign: 'right' }}>Цена</th>
                <th>Создан</th>
                <th style={{ width: 110 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} onClick={() => nav(`/admin/products/${p.id}`)}>
                  <td>
                    <span
                      className={'chip no-dot ' + (p.isActive ? 'chip-ok' : 'chip-neutral')}
                      style={{ fontSize: 10.5, height: 18, padding: '0 6px' }}
                    >
                      {p.isActive ? 'on' : 'off'}
                    </span>
                  </td>
                  <td>
                    <div style={{ fontWeight: 500 }}><bdi>{p.name}</bdi></div>
                    {p.shortUrl && (
                      <div
                        style={{
                          fontSize: 11.5,
                          color: 'var(--fg-subtle)',
                          fontFamily: 'var(--font-mono)',
                          marginTop: 2,
                        }}
                      >
                        {p.shortUrl.replace('https://', '')}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="mono subtle" style={{ fontSize: 12.5 }}>
                      {p.slug}
                    </span>
                  </td>
                  <td className="num" style={{ textAlign: 'right' }}>
                    ₪ {fmtAmountPlain(p.priceIls)}
                  </td>
                  <td>
                    <span className="subtle" style={{ fontSize: 12 }}>
                      {fmtDate(p.createdAt)}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSnippetFor(p);
                      }}
                    >
                      <Icon name="copy" size={13} />
                      Сниппет
                    </button>
                  </td>
                </tr>
              ))}
              {items && filtered.length === 0 && (
                <tr className="no-hover">
                  <td
                    colSpan={6}
                    style={{ textAlign: 'center', color: 'var(--fg-subtle)', padding: 32 }}
                  >
                    {items.length === 0 ? (
                      <>
                        Ни одного продукта нет.{' '}
                        <Link to="/admin/products/new" style={{ textDecoration: 'underline' }}>
                          Создать первый
                        </Link>
                      </>
                    ) : q ? (
                      'Ничего не найдено'
                    ) : tab === 'active' ? (
                      'Нет активных продуктов'
                    ) : (
                      'Нет выключенных продуктов'
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {snippetFor && <SnippetModal product={snippetFor} onClose={() => setSnippetFor(null)} />}
    </>
  );
}

function SnippetModal({
  product,
  onClose,
}: {
  product: Product;
  onClose: () => void;
}): JSX.Element {
  const [tab, setTab] = useState<'snippet' | 'link'>('snippet');
  const [snippet, setSnippet] = useState<Snippet | null>(null);

  useEffect(() => {
    void api
      .getSnippet(product.id)
      .then(setSnippet)
      .catch(() => undefined);
  }, [product.id]);

  const fallbackSnippet =
    `<div data-pay-product="${product.slug}"></div>\n` +
    `<${''}script src="${window.location.origin}/widget.js" defer></${''}script>`;
  const snippetText = snippet?.snippet ?? fallbackSnippet;
  const standaloneUrl = snippet?.standaloneUrl ?? `${window.location.origin}/p/${product.slug}`;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,18,10,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{ width: 640, maxWidth: '100%', boxShadow: 'var(--shadow-lg)' }}
      >
        <div className="card-header">
          <div>
            <div className="card-title">Встроить на сайт</div>
            <div className="card-subtitle"><bdi>{product.name}</bdi></div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} type="button">
            <Icon name="x" size={14} />
          </button>
        </div>
        <div style={{ padding: '0 20px' }}>
          <div className="tabs">
            <button
              type="button"
              className={'tab ' + (tab === 'snippet' ? 'active' : '')}
              onClick={() => setTab('snippet')}
            >
              HTML-сниппет
            </button>
            <button
              type="button"
              className={'tab ' + (tab === 'link' ? 'active' : '')}
              onClick={() => setTab('link')}
            >
              Ссылки
            </button>
          </div>
        </div>
        <div style={{ padding: 20 }}>
          {tab === 'snippet' && (
            <div className="stack">
              <div className="subtle" style={{ fontSize: 13 }}>
                Вставьте в HTML-блок сайта в то место, где должна появиться форма оплаты.
              </div>
              <CopyCodeBlock text={snippetText} />
            </div>
          )}
          {tab === 'link' && (
            <div className="stack">
              <div className="field">
                <div className="field-label">Ссылка на страницу оплаты</div>
                <CopyCodeBlock text={standaloneUrl} />
              </div>
              <div className="field">
                <div className="field-label">Короткая ссылка</div>
                {snippet?.shortUrl || product.shortUrl ? (
                  <CopyCodeBlock text={snippet?.shortUrl ?? product.shortUrl ?? ''} />
                ) : (
                  <div
                    style={{
                      padding: '12px 14px',
                      border: '1px dashed var(--border)',
                      borderRadius: 'var(--radius)',
                      color: 'var(--fg-subtle)',
                      fontSize: 13,
                    }}
                  >
                    Короткая ссылка ещё не создана.
                  </div>
                )}
                <div className="row" style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={async () => {
                      try {
                        const fresh = await api.regenerateShortUrl(product.id);
                        const s = await api.getSnippet(fresh.id);
                        setSnippet(s);
                      } catch (err) {
                        const e = err as { status?: number; details?: { error?: string } | string };
                        const msg = e.status === 502
                          ? 'Не получилось создать короткую ссылку. Проверь у профиля ShortURL API URL + API Key в разделе «Профили».'
                          : `Ошибка: ${typeof e.details === 'string' ? e.details : e.details?.error ?? 'unknown'}`;
                        alert(msg);
                      }
                    }}
                  >
                    <Icon name="refresh" size={13} />
                    {snippet?.shortUrl || product.shortUrl
                      ? 'Перегенерировать'
                      : 'Создать короткую ссылку'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
